import { Typography, Modal, Row, Button } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import uniqBy from 'lodash/uniqBy';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { Form, AttachExcel } from '@/components';
import { DATE_FORMAT_MOMENT } from '@/platform';
import { FormValueType, useSchemaReport } from './hooks/useSchemaReport';
import { ProjectDto } from '@/core';
import classes from './ReportGenerationForm.module.scss';

type PropsType = {
  projectData: ProjectDto;
  handleSubmit: (value: FormValueType) => Promise<void>;
  excelFiles: File[];
  setExcelFiles: (files: File[]) => void;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  errors: string[];
};

const EXCEL_FILES_ACCEPT_ATTRIBUTE =
  '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel';

export const ReportGenerationForm = ({
  projectData,
  handleSubmit,
  excelFiles,
  setExcelFiles,
  isModalOpen,
  setIsModalOpen,
  errors,
}: PropsType) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.project' });
  const reportName = `${projectData.name}_${moment(Date.now()).format(DATE_FORMAT_MOMENT)}`;
  const initialValues = { name: reportName };
  const schemaReport = useSchemaReport(projectData);

  const storeFiles = (file: File) => setExcelFiles(uniqBy(excelFiles.concat(file), 'name'));

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const deleteFile = (deletedFile: File) =>
    setExcelFiles(excelFiles.filter((file) => !isEqual(file, deletedFile)));

  return (
    <Modal
      title={t('generateReportTitle')}
      visible={isModalOpen}
      destroyOnClose={true}
      onCancel={handleCancel}
      width={485}
      footer={
        <Row className={classes.modalControls} justify="space-between">
          <Button key="cancel" type="default" size="large" onClick={handleCancel}>
            {t('cancelBtn')}
          </Button>
          <Form.Submit
            uid="generate-report"
            type="primary"
            disabled={!excelFiles.length || !!errors.length}
            className={classes.submitBtn}
            size="large"
          >
            {t('generateReportBtn')}
          </Form.Submit>
        </Row>
      }
    >
      <div>
        <Form.Formik<FormValueType>
          uid="generate-report"
          i18nKeyPrefix="projects.project"
          initialValues={initialValues}
          validationSchema={schemaReport}
          onSubmit={handleSubmit}
        >
          <div className={classes.form}>
            <Form.Input name="name" allowClear required placeholder={t('placeholder.name')} />
          </div>
        </Form.Formik>
      </div>
      <div className={classes.attachWrapper}>
        <Typography.Text className={classes.attachLabel}>Upload source file</Typography.Text>
        <AttachExcel
          store={storeFiles}
          files={excelFiles}
          accept={EXCEL_FILES_ACCEPT_ATTRIBUTE}
          remove={deleteFile}
        />
      </div>
      {errors.length ? (
        <div className={classes.errorsWrapper}>
          <Typography.Text strong>
            <InfoCircleFilled className={classes.infoIcon} />
            Report error(s):
          </Typography.Text>
          {errors.map((error, index) => (
            <Row>
              <Typography.Text>- {error.replace('Error:', '')}</Typography.Text>
            </Row>
          ))}
        </div>
      ) : null}
    </Modal>
  );
};
